/**
 * Shared template for urgent-care city landing pages.
 *
 * Used by:
 *   • /urgent-medical-care-ho-chi-minh
 *   • /urgent-medical-care-hanoi
 *   • /urgent-medical-care-nha-trang
 *   • /urgent-medical-care-phu-quoc
 *
 * NOT used by:
 *   • /urgent-medical-care-hoi-an  (has its own dedicated, live page)
 *   • /urgent-medical-care-phu-quoc (still uses UrgentMedicalCareComingSoonPage)
 *   • /urgent-medical-care-da-nang (301 → /urgent-medical-care-hoi-an)
 *
 * The template is fully data-driven from `URGENT_CITY_CONTENT` in
 * /src/data/urgentCareContent.js + per-city FAQ extensions merged on top of
 * the pillar FAQs.
 *
 * Safety guardrails:
 *   • Coverage reads "enquiry-by-enquiry coordination" — never "live"
 *   • Each section includes the "service availability subject to confirmation"
 *     wording
 *   • No partner-hospital names, no fixed response-time promises, no 24/7 claims
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Stethoscope, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentTrustStrip, UrgentIntro, UrgentCardGrid, UrgentSteps,
  UrgentFaq, UrgentEditorial, UrgentCtaBand, CoordinationDisclaimer,
  UrgentConditionsServices,
} from '../components/UrgentCareSections';
import {
  URGENT_PILLAR, URGENT_CITY_CONTENT, URGENT_PROMISE,
} from '../data/urgentCareContent';

const SITE = 'https://vietclinics.com';

/**
 * @param {Object} props
 * @param {string} props.citySlug - 'ho-chi-minh' | 'hanoi' | 'nha-trang'
 */
const UrgentMedicalCareCityPage = ({ citySlug }) => {
  // Allow both prop-driven (preferred) and route-param-driven usage.
  const params = useParams();
  const slug = citySlug || params.citySlug;
  const city = URGENT_CITY_CONTENT[slug];

  // Defensive: if a wrong slug ever reaches this template, send to the pillar.
  if (!city || city.coverageStatus !== 'expanding') {
    return <Navigate to="/urgent-medical-care" replace />;
  }

  const canonical = `${SITE}${city.href}`;

  // Main urgent-care enquiry link — routes to the existing SOS form on the
  // pillar page with this city preselected. Urgent enquiries never go to the
  // general treatment form.
  const enquiryHref = `/urgent-medical-care?city=${encodeURIComponent(city.cityName)}#urgent-sos`;

  // Merge pillar FAQs with city-specific FAQ extensions (city extras first).
  const mergedFaq = [...(city.faqExtra || []), ...URGENT_PILLAR.faq];

  // Use pillar service list (city-agnostic) for "what we coordinate".
  const services = URGENT_PILLAR.services;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'MedicalBusiness',
        '@id': `${canonical}#business`,
        name: `VietClinics — Urgent Medical Care in ${city.cityName}`,
        description: city.metaDescription,
        url: canonical,
        areaServed: [{ '@type': 'City', name: city.cityName, addressCountry: 'VN' }],
        medicalSpecialty: ['GeneralPractice'],
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: city.metaTitle,
        description: city.metaDescription,
        inLanguage: 'en',
        dateModified: URGENT_PILLAR.editorial.lastUpdated,
        lastReviewed: URGENT_PILLAR.editorial.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: URGENT_PILLAR.editorial.reviewedBy },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: mergedFaq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Urgent Medical Care', item: `${SITE}/urgent-medical-care` },
          { '@type': 'ListItem', position: 3, name: city.cityName, item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{city.metaTitle}</title>
        <meta name="description" content={city.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={city.metaTitle} />
        <meta property="og:description" content={city.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={city.heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid={`urgent-city-hero-${slug}`}>
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)',
          }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                {city.eyebrow}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                {city.title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-5">
                {city.subtitle}
              </p>
              <div className="mb-5">
                <Link
                  to={enquiryHref}
                  className="inline-flex items-center gap-2 text-white font-bold rounded-2xl px-7 py-4 transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                    boxShadow: '0 12px 24px rgba(31,167,161,0.25)',
                  }}
                  data-testid={`urgent-city-enquiry-${slug}`}
                >
                  <Stethoscope className="h-4 w-4" />
                  Get a 24/7 Doctor Call-Out
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              {/* Promise banner */}
              <div className="bg-[#F0FAF8] border border-[#DCEFEC] rounded-xl px-4 py-3 max-w-xl mb-5 text-sm text-slate-700" data-testid={`urgent-city-availability-${slug}`}>
                <strong className="text-[#0B4F65]">{URGENT_PROMISE}</strong>
                {city.availabilityNote && <span className="block mt-1 text-slate-600">{city.availabilityNote}</span>}
              </div>
              <div className="max-w-xl">
                <CoordinationDisclaimer testId={`urgent-city-disclaimer-${slug}`} />
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden bg-slate-100" style={{ aspectRatio: '4 / 3' }}>
              <img
                src={city.heroImage}
                onError={(e) => { e.currentTarget.src = city.heroFallback; }}
                alt={`VietClinics urgent care coordination in ${city.cityName}`}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      <UrgentTrustStrip />

      <UrgentIntro
        heading={`24/7 doctor call-outs in ${city.cityName}`}
        paragraphs={city.introParagraphs}
        testId={`urgent-city-intro-${slug}`}
      />

      <UrgentCardGrid
        eyebrow="What we treat"
        heading={`What a visiting doctor can treat in ${city.cityName}`}
        items={services}
        testId={`urgent-city-services-${slug}`}
      />

      <UrgentConditionsServices testId={`urgent-city-conditions-${slug}`} />

      <UrgentSteps
        eyebrow="How it works"
        heading="From enquiry to aftercare in four steps"
        steps={URGENT_PILLAR.howItWorks}
        testId={`urgent-city-steps-${slug}`}
      />

      <UrgentFaq
        heading={`Urgent care in ${city.cityName} — frequently asked`}
        items={mergedFaq}
        testId={`urgent-city-faq-${slug}`}
      />

      <UrgentEditorial
        editorial={URGENT_PILLAR.editorial}
        testId={`urgent-city-editorial-${slug}`}
      />

      <UrgentCtaBand
        heading={`Need a doctor in ${city.cityName}?`}
        subheading={`Tell us your symptoms and your address in ${city.cityName} — a licensed English-speaking doctor arrives within 60 minutes, 24/7.`}
        primaryCta={{ to: enquiryHref, label: 'Get a 24/7 Doctor Call-Out' }}
        testId={`urgent-city-cta-${slug}`}
      />

      <Footer />
    </div>
  );
};

export default UrgentMedicalCareCityPage;
